import React from 'react';
import { Box, Typography, Button } from '@drone-js/core';
import { getImageUrl } from 'core/utils/activityUrlGenerator';
import { useSelector } from 'react-redux';
import { selectCmsFromBootstrap, selectBrowserInfoFromBootstrap } from 'store/slice/bootstrap';
import { openWindowUrl } from 'core/window/windowService';
import { getGoogleMapUrl } from 'core/service/map/googleMap';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesLocationMapImageClickEventActivitiesFieldsV1,
    ActivitiesLocationMapImageRevealEventActivitiesFieldsV1,
    ActivitiesLocationViewMapButtonClickEventActivitiesFieldsV1,
    ActivitiesLocationViewMapButtonRevealEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesLocationMapImageClickEventActivitiesManifestV1,
    ActivitiesLocationMapImageRevealEventActivitiesManifestV1,
    ActivitiesLocationViewMapButtonClickEventActivitiesManifestV1,
    ActivitiesLocationViewMapButtonRevealEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { selectScreenSize } from 'store/slice/screen';
import { ActivityImage } from 'component/common/ActivityImage/ActivityImage';

export interface LocationDetails {
    locationName: string;
    locationAddress: string;
    lat: number;
    long: number;
}

interface LocationCardProps {
    locationDetails: LocationDetails;
}

// eslint-disable-next-line @agoda/agoda-a11y/missing-keyboard-tests -- @agoda/agoda-a11y/missing-keyboard-tests[2c92fdc0-43e7-49c4-a0aa-1bb6f24c2032]
const LocationCard: React.FC<LocationCardProps> = (props) => {
    const cms = useSelector(selectCmsFromBootstrap);
    const browser = useSelector(selectBrowserInfoFromBootstrap);
    const screenSize = useSelector(selectScreenSize);
    const isShowViewMapButton = !browser.isWebView;

    trackElementEvent<ActivitiesLocationViewMapButtonRevealEventActivitiesFieldsV1>(ActivitiesLocationViewMapButtonRevealEventActivitiesManifestV1.ids, {});
    trackElementEvent<ActivitiesLocationMapImageRevealEventActivitiesFieldsV1>(ActivitiesLocationMapImageRevealEventActivitiesManifestV1.ids, {});
    const handleMapButtonClick = () => {
        const getUrl = getGoogleMapUrl(props.locationDetails.lat, props.locationDetails.long);
        trackElementEvent<ActivitiesLocationViewMapButtonClickEventActivitiesFieldsV1>(ActivitiesLocationViewMapButtonClickEventActivitiesManifestV1.ids, {});
        openWindowUrl(getUrl, '_blank');
    };

    const handleImageClick = () => {
        trackElementEvent<ActivitiesLocationMapImageClickEventActivitiesFieldsV1>(ActivitiesLocationMapImageClickEventActivitiesManifestV1.ids, {});
    };

    // TODO: Extract to a new component
    const renderDesktopLocationCard = () => (
        // eslint-disable-next-line no-restricted-syntax
        <Box pt="8" display="flex" data-testid="desktop-location-card">
            <div>
                {/* eslint-disable-next-line @agoda/agoda-a11y/no-incorrect-props, @agoda/agoda-a11y/no-hardcoded-content -- @agoda/agoda-a11y/no-incorrect-props[e889db8e-0246-474d-b7e7-da454bd76ca6], @agoda/agoda-a11y/no-hardcoded-content[3aab1d92-de8d-4bf2-a7ec-2f2d841f8864] */}
                <ActivityImage
                    width="64px"
                    height="64px"
                    src={getImageUrl('details-location-map-img.svg')}
                    alt="details-location-map"
                    borderRadius="16"
                    onClick={handleImageClick}
                />
            </div>
            <Box px="16" maxWidth="430px">
                <Typography variant="body-default-strong" textColor="base.default">
                    {props.locationDetails.locationName}
                </Typography>
                <Typography variant="body-default" textColor="base.subtle" mt="12">
                    {props.locationDetails.locationAddress}
                </Typography>
            </Box>
            {isShowViewMapButton && (
                <Box width="200px" ms="auto" display="flex">
                    <Button type="button" variant="label" width="parent-fit" hierarchy="secondary" mt="8" size="m" ms="auto" label={cms[207350]} onClick={handleMapButtonClick} />
                </Box>
            )}
        </Box>
    );

    // TODO: Extract to a new component
    const renderMobileLocationCard = () => (
        // eslint-disable-next-line no-restricted-syntax
        <div data-testid="mobile-location-card">
            <Box pt="8" display="flex">
                <div>
                    {/* eslint-disable-next-line @agoda/agoda-a11y/no-incorrect-props, @agoda/agoda-a11y/no-hardcoded-content -- @agoda/agoda-a11y/no-incorrect-props[24b1fa2d-69df-49dc-aa7b-a1db8f6cb41d], @agoda/agoda-a11y/no-hardcoded-content[a19b3bfb-713a-494a-9ab4-461277f463dc] */}
                    <ActivityImage
                        width="64px"
                        height="64px"
                        src={getImageUrl('details-location-map-img.svg')}
                        alt="details-location-map"
                        borderRadius="16"
                        onClick={handleImageClick}
                    />
                </div>
                <Box px="16">
                    <Typography variant="body-default-strong" textColor="base.default">
                        {props.locationDetails.locationName}
                    </Typography>
                    <Typography variant="body-default" textColor="base.subtle" mt="12">
                        {props.locationDetails.locationAddress}
                    </Typography>
                </Box>
            </Box>
            {isShowViewMapButton && (
                <Box mt="12">
                    <Button type="button" variant="label" width="parent-fit" hierarchy="secondary" size="s" ms="auto" label={cms[207350]} onClick={handleMapButtonClick} />
                </Box>
            )}
        </div>
    );

    return screenSize.mobile ? renderMobileLocationCard() : renderDesktopLocationCard();
};
LocationCard.displayName = 'LocationCard';

export default LocationCard;
